import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
    return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

/**
 * Formats a date-only value (a `DATE` column like "2026-07-17") without timezone
 * drift. `new Date("2026-07-17")` parses as UTC midnight, so formatting in a
 * US-local timezone shifts it back a day; pinning `timeZone: "UTC"` keeps the
 * calendar date intact. Use this for stored `DATE` values, not timestamps.
 */
export function formatDateOnly(date: string | Date): string {
    return new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
    });
}

export function formatDateTime(date: string | Date): string {
    return new Date(date).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

/**
 * Pluralize a noun against a count. Returns `singular` when count === 1,
 * otherwise `plural` (defaulting to `singular + "s"`). Does NOT include the
 * number itself, so callers control formatting, e.g. `${n} ${pluralize(n, "job")}`.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
    return count === 1 ? singular : (plural ?? `${singular}s`);
}

/**
 * Format a quantity or points value for humans. Postgres `numeric` columns
 * arrive as strings like "1.0000"; trim to at most `maxDecimals` decimal
 * places and drop trailing zeros ("1.0000" → "1", "2.5000" → "2.5").
 * Non-numeric input is rendered as-is rather than hidden.
 */
export function formatQuantity(value: number | string | null | undefined, maxDecimals = 2): string {
    if (value == null || value === "") return "—";
    const n = typeof value === "string" ? Number(value) : value;
    if (!Number.isFinite(n)) return String(value);
    return String(Number(n.toFixed(maxDecimals)));
}

export function formatCurrency(cents: number): string {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(cents / 100);
}

export function formatDollars(dollars: number | string): string {
    const value = typeof dollars === "string" ? Number(dollars) : dollars;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(Number.isFinite(value) ? value : 0);
}

export { lifecycleStatusColor as statusColor } from "./lifecycle";
